import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from "react-router-dom";
import { getArticleBySlug } from "../../../services/article.service";
import { searchArticle } from "../../../services/search.service";
import translations from '../../../data/translationContent';

const REACT_APP_BASE_URL = process.env.REACT_APP_BASE_URL;

const getBibliographyList = (bibliography_art) => {
    if (!bibliography_art) return [];
    if (Array.isArray(bibliography_art)) return bibliography_art;
    return bibliography_art
        .split(/\r?\n/)
        .map(item => item.trim())
        .filter(item => item.length > 0);
};

const getImagePath = (photoPath) => {
    if (!photoPath) return '/placeholder-image.jpg';
    return photoPath.startsWith('http') ? photoPath : `${REACT_APP_BASE_URL}/${photoPath}`;
};

const isImage = (type_doc, path_doc) => {
    const imgExts = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'];
    const ext = (path_doc || '').split('.').pop()?.toLowerCase();
    return imgExts.includes(ext) || (type_doc && type_doc.toLowerCase().includes('image'));
};
const isPdf = (path_doc) => (path_doc || '').toLowerCase().endsWith('.pdf');
const isWord = (path_doc) => (path_doc || '').toLowerCase().endsWith('.doc') || (path_doc || '').toLowerCase().endsWith('.docx');
const isExcel = (path_doc) => (path_doc || '').toLowerCase().endsWith('.xls') || (path_doc || '').toLowerCase().endsWith('.xlsx');
const isPowerPoint = (path_doc) => (path_doc || '').toLowerCase().endsWith('.ppt') || (path_doc || '').toLowerCase().endsWith('.pptx');
const isText = (path_doc) => (path_doc || '').toLowerCase().endsWith('.txt');

const docTypeTabs = [
    {
        key: 'images',
        label: { fr: "Images", en: "Images", mg: "Sary" },
        filter: (doc) => isImage(doc.type_doc, doc.path_doc),
        color: "border-lime-400 bg-lime-50"
    },
    {
        key: 'pdfs',
        label: { fr: "PDFs", en: "PDFs", mg: "PDFs" },
        filter: (doc) => isPdf(doc.path_doc),
        color: "border-red-400 bg-red-50"
    },
    {
        key: 'words',
        label: { fr: "Word", en: "Word", mg: "Word" },
        filter: (doc) => isWord(doc.path_doc),
        color: "border-blue-400 bg-blue-50"
    },
    {
        key: 'excels',
        label: { fr: "Excel", en: "Excel", mg: "Excel" },
        filter: (doc) => isExcel(doc.path_doc),
        color: "border-green-400 bg-green-50"
    },
    {
        key: 'powerpoints',
        label: { fr: "PowerPoint", en: "PowerPoint", mg: "PowerPoint" },
        filter: (doc) => isPowerPoint(doc.path_doc),
        color: "border-orange-400 bg-orange-50"
    },
    {
        key: 'texts',
        label: { fr: "Textes", en: "Texts", mg: "Soratra" },
        filter: (doc) => isText(doc.path_doc),
        color: "border-gray-400 bg-gray-50"
    },
    {
        key: 'others',
        label: { fr: "Autres", en: "Others", mg: "Hafa" },
        filter: (doc) =>
            !isImage(doc.type_doc, doc.path_doc) &&
            !isPdf(doc.path_doc) &&
            !isWord(doc.path_doc) &&
            !isExcel(doc.path_doc) &&
            !isPowerPoint(doc.path_doc) &&
            !isText(doc.path_doc),
        color: "border-yellow-400 bg-yellow-50"
    }
];

const hasLang = (obj, lang) => obj && obj[lang] && (
    Array.isArray(obj[lang])
        ? obj[lang].length > 0
        : (typeof obj[lang] === 'string' ? obj[lang].trim() !== '' : false)
);

const ArticleDocumentsTabs = ({
    documents = [],
    language = 'fr',
    t = (x) => x,
    onShowImage = () => { },
}) => {
    // Filter tabs to only those with docs
    const tabs = docTypeTabs
        .map(tab => ({
            ...tab,
            docs: documents.filter(tab.filter)
        }))
        .filter(tab => tab.docs.length > 0);

    const [activeTab, setActiveTab] = useState(tabs.length > 0 ? tabs[0].key : "");
    // Control how many items are shown per tab (default 3, then show more)
    const [showCount, setShowCount] = useState(() => {
        const initial = {};
        tabs.forEach(tab => { initial[tab.key] = 3; });
        return initial;
    });

    // When tab changes, reset showCount for the new tab
    useEffect(() => {
        if (activeTab && !showCount[activeTab]) {
            setShowCount(sc => ({ ...sc, [activeTab]: 3 }));
        }
    }, [activeTab, showCount]);

    if (!tabs.length) return null;

    return (
        <div>
            <div className="flex border-b mb-4 gap-2 overflow-x-auto">
                {tabs.map(tab => (
                    <button
                        key={tab.key}
                        className={`px-4 py-2 border-b-2 font-semibold text-sm whitespace-nowrap ${tab.key === activeTab ? tab.color + ' border-b-4' : 'border-transparent'
                            } hover:cursor-pointer`}
                        style={{ outline: "none" }}
                        onClick={() => setActiveTab(tab.key)}
                        type="button"
                    >
                        {tab.label[language] || tab.label.fr} ({tab.docs.length})
                    </button>
                ))}
            </div>
            <div>
                {tabs.map(tab => (
                    tab.key === activeTab && (
                        <div key={tab.key}>
                            <div className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4`}>
                                {tab.docs.slice(0, showCount[tab.key] || 3).map((doc, idx) => (
                                    <div key={doc.id_doc || idx} className={`border rounded-lg overflow-hidden ${tab.color} p-3 flex flex-col`}>
                                        <div className="flex mb-2 items-center gap-2">
                                            <strong className="truncate">{doc.title_doc}</strong>
                                            <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-greenprimary text-white">{doc.type_doc}</span>
                                        </div>
                                        <div className="text-xs text-gray-700 mb-1">
                                            {doc.auth_doc && (
                                                <div>
                                                    <span className="font-semibold">{t("authorLabel")}</span>
                                                    <span className="ml-1">{doc.auth_doc}</span>
                                                </div>
                                            )}
                                        </div>
                                        {isImage(doc.type_doc, doc.path_doc) ? (
                                            <button
                                                type="button"
                                                onClick={() => onShowImage(getImagePath(doc.path_doc), doc.title_doc)}
                                                className="block w-full hover:cursor-pointer"
                                                tabIndex={0}
                                            >
                                                <img
                                                    src={getImagePath(doc.path_doc)}
                                                    alt={doc.title_doc}
                                                    className="w-full h-auto rounded-md max-h-[150px] object-contain mx-auto"
                                                />
                                                <span className="block text-center mt-1 text-green-700 text-xs">{t("seeImage")}</span>
                                            </button>
                                        ) : (
                                            <a
                                                href={getImagePath(doc.path_doc)}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="inline-block mt-2 px-3 py-1 bg-greenprimary text-white rounded hover:bg-greenlogo hover:cursor-pointer text-xs"
                                            >
                                                {isPdf(doc.path_doc) ? t("viewPdf") : t("download")}
                                            </a>
                                        )}
                                        {doc.source_doc && Object.keys(doc.source_doc).length > 0 && (
                                            <div className="mt-2 text-xs">
                                                <span className="font-semibold">{t("sourcesLabel")}</span>
                                                <div>
                                                    {Object.entries(doc.source_doc).map(([key, value]) => (
                                                        <div key={key}>
                                                            <span className="font-medium text-greenprimary">{key}:</span> {value}
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                            {(tab.docs.length > (showCount[tab.key] || 3)) && (
                                <div className="flex justify-center mt-4">
                                    <button
                                        className="px-5 py-2 rounded bg-greenlogo text-white font-semibold hover:bg-greenprimary hover:cursor-pointer text-sm"
                                        onClick={() => setShowCount(sc => ({
                                            ...sc,
                                            [tab.key]: (sc[tab.key] || 3) + 3
                                        }))}
                                    >
                                        {t("seeMore") || "Voir plus"}
                                    </button>
                                </div>
                            )}
                        </div>
                    )
                ))}
            </div>
        </div>
    );
};

const ArticleDetailsContent = () => {
    const { id } = useParams();
    const [article, setArticle] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [language, setLanguage] = useState('fr');
    const [imageModal, setImageModal] = useState({ open: false, src: "", alt: "" });
    const navigate = useNavigate();

    const t = (key) => translations[language][key];

    useEffect(() => {
        const fetchArticle = async () => {
            try {
                setLoading(true);
                setError(false);
                const response = await getArticleBySlug(id);
                if (response && response.data) {
                    setArticle(response.data);
                } else {
                    setError(true);
                }
            } catch (err) {
                setError(true);
            } finally {
                setLoading(false);
            }
        };
        fetchArticle();
    }, [id]);

    const getAvailableLanguages = () => {
        if (!article) return [];
        const langs = new Set();
        ['fr', 'en', 'mg'].forEach(l => {
            if (
                hasLang(article?.resume_art, l) ||
                hasLang(article?.corp_art, l) ||
                hasLang(article?.key_words_art, l)
            ) langs.add(l);
        });
        return Array.from(langs);
    };

    const hasContentInSelectedLanguage = (content) => {
        return content && content[language] && content[language].trim() !== '';
    };

    const availableLanguages = getAvailableLanguages();
    const isMonoLingual = availableLanguages.length <= 1;

    const handleKeywordClick = async (keyword) => {
        try {
            await searchArticle({ q: keyword });
            navigate(`/recherche?keyword=${encodeURIComponent(keyword)}`);
        } catch (err) {
            navigate(`/recherche?keyword=${encodeURIComponent(keyword)}`);
        }
    };

    if (loading) {
        return (
            <div className="text-center py-10">
                <h1 className="text-3xl font-bold text-greenprimary">{t("loading")}</h1>
            </div>
        );
    }

    if (error || !article) {
        return (
            <div className="text-center py-10">
                <h1 className="text-3xl font-bold text-red-600">{t("notFound")}</h1>
                <p className="text-gray-500 mt-4">{t("notFoundDesc")}</p>
            </div>
        );
    }

    return (
        <div className="max-w-7xl mx-auto p-6">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
                <h2 className="text-3xl sm:text-4xl font-semibold flex items-center">
                    <span className="w-10 sm:w-16 h-1.5 sm:h-2.5 mt-2 sm:mt-3 bg-greenlogo mr-2"></span>
                    {t("summary")}
                </h2>
                <div className="flex w-full sm:w-auto">
                    <button
                        onClick={() => setLanguage('fr')}
                        disabled={isMonoLingual && language !== 'fr'}
                        className={`flex-1 sm:flex-none items-center justify-center px-4 sm:px-5 py-2 font-bold sm:w-32
                            ${language === 'fr' ? 'text-greenprimary border-b-4 border-danger' : 'bg-greenlogo/50 text-greenprimary'}
                            ${isMonoLingual && language !== 'fr' ? 'cursor-not-allowed' : 'cursor-pointer'}
                        `}
                    >
                        Français
                    </button>
                    <button
                        onClick={() => setLanguage('en')}
                        disabled={isMonoLingual && language !== 'en'}
                        className={`flex-1 sm:flex-none items-center justify-center px-4 sm:px-5 py-2 font-bold sm:w-32
                            ${language === 'en' ? 'text-greenprimary border-b-4 border-danger' : 'bg-greenlogo/50 text-greenprimary'}
                            ${isMonoLingual && language !== 'en' ? 'cursor-not-allowed' : 'cursor-pointer'}
                        `}
                    >
                        English
                    </button>
                    <button
                        onClick={() => setLanguage('mg')}
                        disabled={isMonoLingual && language !== 'mg'}
                        className={`flex-1 sm:flex-none items-center justify-center px-4 sm:px-5 py-2 font-bold sm:w-32
                            ${language === 'mg' ? 'text-greenprimary border-b-4 border-danger' : 'bg-greenlogo/50 text-greenprimary'}
                            ${isMonoLingual && language !== 'mg' ? 'cursor-not-allowed' : 'cursor-pointer'}
                        `}
                    >
                        Malagasy
                    </button>
                </div>
            </div>
            <div className="mt-6">
                {article?.resume_art?.[language] ? (
                    <p className="leading-relaxed text-justify">
                        {article.resume_art[language]}
                    </p>
                ) : (
                    <p className="italic text-gray-500 mb-6">
                        {t("noSummary")}
                    </p>
                )}

                <h2 className="text-3xl sm:text-4xl font-semibold flex items-center mt-10 mb-6">
                    <span className="w-10 sm:w-16 h-1.5 sm:h-2.5 mt-2 sm:mt-3 bg-greenlogo mr-2"></span>
                    {t("keywords")}
                </h2>
                {article?.key_words_art?.[language]?.length > 0 ? (
                    <div className="flex flex-wrap gap-2 mb-6">
                        {article.key_words_art[language].map((keyword, index) => (
                            <button
                                key={index}
                                type="button"
                                className="bg-black/5 px-4 py-0.5 rounded-md text-sm font-light border border-black/10 hover:bg-greenlogo hover:text-white transition cursor-pointer"
                                onClick={() => handleKeywordClick(keyword)}
                            >
                                {keyword}
                            </button>
                        ))}
                    </div>
                ) : (
                    <p className="italic text-gray-500 mb-6">
                        {t("noKeywords")}
                    </p>
                )}

                <h2 className="text-3xl sm:text-4xl font-semibold flex items-center mt-10 mb-6">
                    <span className="w-10 sm:w-16 h-1.5 sm:h-2.5 mt-2 sm:mt-3 bg-greenlogo mr-2"></span>
                    {t("content")}
                </h2>
                {hasContentInSelectedLanguage(article?.corp_art) ? (
                    <div
                        className="leading-relaxed text-justify article-content ql-editor"
                        dangerouslySetInnerHTML={{
                            __html: article.corp_art[language]
                        }}
                    />
                ) : (
                    <p className="italic text-gray-500 mb-6">
                        {t("content") + " " + t("noSummary")}
                    </p>
                )}

                {article?.documents && article.documents.length > 0 && (
                    <div className="mt-14 mb-14">
                        <h2 className="text-3xl sm:text-4xl font-semibold flex items-center mt-10 mb-6">
                            <span className="w-10 sm:w-16 h-1.5 sm:h-2.5 mt-2 sm:mt-3 bg-greenlogo mr-2"></span>
                            {t("additionalDocs")}
                        </h2>
                        <ArticleDocumentsTabs
                            documents={article.documents}
                            language={language}
                            t={t}
                            onShowImage={(src, alt) => setImageModal({ open: true, src, alt })}
                        />
                    </div>
                )}

                {imageModal.open && (
                    <div className="fixed z-50 inset-0 flex items-center justify-center bg-black bg-opacity-90" onClick={() => setImageModal({ open: false, src: "", alt: "" })}>
                        <img
                            src={imageModal.src}
                            alt={imageModal.alt}
                            className="max-h-[94vh] max-w-[94vw] object-contain rounded-lg"
                            onClick={e => e.stopPropagation()}
                        />
                        <button
                            className="absolute top-4 right-4 text-white text-4xl font-bold bg-black bg-opacity-30 rounded-full w-12 h-12 flex items-center justify-center hover:bg-opacity-70 transition"
                            onClick={() => setImageModal({ open: false, src: "", alt: "" })}
                            aria-label="Fermer"
                        >
                            &times;
                        </button>
                    </div>
                )}

                <h2 className="text-3xl sm:text-4xl font-semibold flex items-center mt-10 mb-6">
                    <span className="w-10 sm:w-16 h-1.5 sm:h-2.5 mt-2 sm:mt-3 bg-greenlogo mr-2"></span>
                    {t("author")}
                </h2>
                <dl>
                    <dt className="font-semibold italic">
                        {article?.author?.title_aut} {article?.author?.first_name} {article?.author?.last_name}
                    </dt>
                    <dd className="italic mb-5">
                        {article?.author?.affiliations?.map((affiliation, index) => (
                            <span key={index}>
                                {affiliation.name_aff}
                                {affiliation.country_aff ? ` (${affiliation.country_aff})` : ''}
                                {index < article.author.affiliations.length - 1 && ', '}
                            </span>
                        ))}
                    </dd>
                    {article?.coauthors && article.coauthors.length > 0 && article.coauthors.map((coauthor) => (
                        <div key={coauthor.id_aut} className="mb-5">
                            <dt className="font-semibold italic">
                                {coauthor.title_aut} {coauthor.first_name} {coauthor.last_name}
                            </dt>
                            <dd className="italic">
                                {coauthor.affiliations && coauthor.affiliations.map((affiliation, index) => (
                                    <span key={index}>
                                        {affiliation.name_aff}
                                        {affiliation.country_aff ? ` (${affiliation.country_aff})` : ''}
                                        {index < coauthor.affiliations.length - 1 && ', '}
                                    </span>
                                ))}
                            </dd>
                        </div>
                    ))}
                </dl>

                <h2 className="text-3xl sm:text-4xl font-semibold flex items-center mt-10 mb-6">
                    <span className="w-10 sm:w-16 h-1.5 sm:h-2.5 mt-2 sm:mt-3 bg-greenlogo mr-2"></span>
                    {t("cite")}
                </h2>
                <p className="italic mb-5">
                    {(() => {
                        // Récupérer les auteurs: auteur principal + co-authors éventuels
                        const authors = [];
                        if (article?.author) {
                            // Nom de famille, Prénom (première lettre en majuscule)
                            const nom = article.author.last_name?.charAt(0).toUpperCase() + article.author.last_name?.slice(1);
                            const prenom = article.author.first_name?.charAt(0).toUpperCase() + article.author.first_name?.slice(1);
                            authors.push(`${nom} ${prenom}`);
                        }
                        if (article?.coauthors?.length) {
                            article.coauthors.forEach(co => {
                                const nom = co.last_name?.charAt(0).toUpperCase() + co.last_name?.slice(1);
                                const prenom = co.first_name?.charAt(0).toUpperCase() + co.first_name?.slice(1);
                                authors.push(`${nom} ${prenom}`);
                            });
                        }
                        // Récupérer l'année de publication (ou vide si absente)
                        let year = "";
                        if (article?.date_pub_art) {
                            const dateObj = new Date(article.date_pub_art);
                            year = dateObj.getFullYear();
                        }
                        // Récupérer le titre
                        const titre = article?.title_art?.[language] || article?.title_art?.fr || "";
                        // Récupérer le DOI (ou message si non dispo)
                        const doi = article?.doi_art && article.doi_art !== ""
                            ? article.doi_art
                            : t("doi"); // ou laisse vide si tu préfères

                        // Construction de la citation
                        return (
                            <>{authors.join(", ")}{year ? ` (${year})` : ""}. {titre}. MADATLAS. {doi}</>
                        );
                    })()}
                </p>

                <h2 className="text-3xl sm:text-4xl font-semibold flex items-center mt-10 mb-6">
                    <span className="w-10 sm:w-16 h-1.5 sm:h-2.5 mt-2 sm:mt-3 bg-greenlogo mr-2"></span>
                    {t("researchMaterials")}
                </h2>
                {article?.materiel_rech_art && article.materiel_rech_art.length > 0 ? (
                    <span>{article.materiel_rech_art}</span>
                ) : (
                    <p className="italic text-gray-500 mb-10">
                        {t("noResearchMaterials")}
                    </p>
                )}

                <h2 className="text-3xl sm:text-4xl font-semibold flex items-center mt-10 mb-6">
                    <span className="w-10 sm:w-16 h-1.5 sm:h-2.5 mt-2 sm:mt-3 bg-greenlogo mr-2"></span>
                    {t("bibliographies")}
                </h2>
                {getBibliographyList(article?.bibliography_art).length > 0 ? (
                    <ul className="list-disc ml-6 mb-10">
                        {getBibliographyList(article.bibliography_art).map((item, idx) => (
                            <li key={idx} className="text-base mb-1">{item}</li>
                        ))}
                    </ul>
                ) : (
                    <p className="italic text-gray-500 mb-10">
                        {t("noBibliography")}
                    </p>
                )}
            </div>
        </div>
    );
};

export default ArticleDetailsContent;